import { MinsterConfirmDialogComponent } from '../ui-components/minster-confirm-dialog/minster-confirm-dialog.component';
import { ControlContainer, FormsModule, NgForm, NgModelGroup, ReactiveFormsModule } from '@angular/forms';
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from '../ui-components/minster-select/minster-select.component';
import { MinsterInputComponent } from '../ui-components/minster-input/minster-input.component';
import { MinsterCardComponent } from '../ui-components/minster-card/minster-card.component';
import { MinsterTelComponent } from '../ui-components/minster-tel/minster-tel.component';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { InputType, ScreenType } from '../../models/enums.models';
import { MatExpansionModule } from '@angular/material/expansion';
import { DialogModel } from '../../models/dialog-model.model';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { Occupant } from '../../models/claim.model';
import { LookUp } from '../../models/look-up.model';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'app-passengers',
    standalone: true,
    imports: [MinsterInputComponent, FormsModule, MatCardModule, MatButtonModule, MatExpansionModule, CommonModule, MinsterTelComponent,
        MatIconModule, MinsterCardComponent, MatSelectModule, ReactiveFormsModule, MatInputModule, MinsterSelectComponent, MinsterCheckboxComponent],
    templateUrl: './passengers.component.html',
    styleUrl: './passengers.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class PassengersComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _inputTypes = InputType;
    _canShareDataWithPassengers = false;
    _passengers: Occupant[];
    _titles: LookUp[];
    _injuryStatuses: LookUp[];
    _telTypes: LookUp[];
    _readonly: boolean = false;
    _numberOfPassengers: number = 0;

    constructor(
        router: Router,
        stateService: StateService,
        toastr: ToastrService,
        private dialog: MatDialog,
        private change: ChangeDetectorRef,
        claimService: ClaimsService
    ) {
        super(router, ScreenType.Passengers, toastr, stateService, claimService);
 
        this._readonly = stateService.formIsSubmitted;
        this._passengers = stateService.claim!.occupants.filter(x => x.isDriver == false);
        this._canShareDataWithPassengers = stateService.claim!.canShareDataWithPassengers;
        this._titles = stateService.lookUps?.lookUpDictionary['Title']!;
        this._injuryStatuses = stateService.lookUps?.lookUpDictionary['PassengerInjuryStatus']!;
        this._telTypes = stateService.lookUps?.lookUpDictionary['TelephoneNumberType']!;
        this._numberOfPassengers = stateService.claim!.numberOfPassengers;
    }

    createNewPassenger(claimId: number): Occupant {
        return {
            occupantId: 0,
            titleId: 0,
            claimId: claimId,
            forename: '',
            surname: '',
            isDriver: false,
            passengerInjuryStatusId: 0,
            occupantContactDetails: [{ telephoneNumber: '', telephoneNumberTypeId: 1, isPrimary: true }],
            accordionOpen: true
        };
    }

    onRemovePassenger(passengerIndex: number): void {
        const passengerToRemove = this._passengers[passengerIndex];
        if (!passengerToRemove) {
            console.warn('Passenger not found.');
            return;
        }

        const message = `Are you sure you want to delete the passenger ${passengerToRemove.forename} ${passengerToRemove.surname}?`;

        this.dialog
            .open(MinsterConfirmDialogComponent, {
                maxWidth: '600px',
                data: new DialogModel('Confirm Deletion', message),
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.stateService.claim!.occupants = this.stateService.claim!.occupants.filter(
                        (occupant) => occupant !== passengerToRemove
                    );

                    this._passengers = this.stateService.claim!.occupants.filter((x) => !x.isDriver);
                    this.change.markForCheck();
                }
            });
    }

    override onNext(): void {
        if(this._readonly){
            super.onNext();
            return;
        }
        let hasErrors = false;

        if (this._formGroup && this._formGroup.invalid) {
            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);

                if (control) {
                    const errors = control.errors;

                    if (errors) {

                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];
                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        this._passengers.forEach(passenger => {
            passenger.occupantContactDetails = passenger.occupantContactDetails!.filter(contact =>
                contact.telephoneNumber?.trim() !== ''
            );
        });

        this.stateService.enableSaving = this.hasStateChanged();

        super.onNext();
    }

    onAddPassenger(): void {
        if (this._passengers.length >= 10) {
            this.toastr.warning('You can only add up to 10 passengers.');
            return;
        }

        const newPassenger = this.createNewPassenger(this.stateService.claim!.claimDetailId);
        this.stateService.claim?.occupants.push(newPassenger);
        this._passengers = this.stateService.claim!.occupants.filter(x => x.isDriver == false);
        this.change.markForCheck();
    }

    onAddPhoneNumber(passenger: Occupant): void {
        if (!passenger.occupantContactDetails || passenger.occupantContactDetails.length === 0) {
            passenger.occupantContactDetails = [
                {
                    telephoneNumber: '',
                    telephoneNumberTypeId: 1,
                    isPrimary: true
                }
            ];
            return;
        }

        if (passenger.occupantContactDetails.length >= 2) {
            this.toastr.warning('You can only add one additional phone number.');
            return;
        }

        passenger.occupantContactDetails.push({
            telephoneNumber: '',
            telephoneNumberTypeId: 2,
            isPrimary: false
        });
    }

    onRemoveSecondaryPhoneNumber(passenger: Occupant): void {
        if (passenger.occupantContactDetails!.length > 1) {
            passenger.occupantContactDetails!.splice(1, 1);
        }
        else {
            this.toastr.warning('You cannot remove the primary phone number.');
        }
    }

    canAddPhone(passenger: Occupant): boolean {
        const contactDetails = passenger.occupantContactDetails ?? [];
        return contactDetails.length < 2 && this.isPhoneNumberValid(contactDetails[0]?.telephoneNumber, contactDetails[0]?.telephoneNumberTypeId);
    }
    private isPhoneNumberValid(phoneNumber: string, type: number): boolean {
        const phoneRegex = type === 1 ? /^07[0-9]{8,15}$/ : /^(?:01|02|03|08)[0-9]{8,15}$/;
        return phoneRegex.test(phoneNumber) && phoneNumber?.trim().length > 0;
    }

    getPrimaryTelephoneNumber(index: number): string {
        return this._passengers[index]?.occupantContactDetails?.[0]?.telephoneNumber || '';
    }

    setPrimaryTelephoneNumber(index: number, value: string): void {
        const passengers = this._passengers[index];

        if (passengers?.occupantContactDetails?.[0]) {
            passengers.occupantContactDetails[0].telephoneNumber = value;
            this.updatePrimaryStatus(index);
        }
    }

    getPrimaryPhoneType(index: number): 'mobile' | 'landline' {
        return this.getPrimaryTelephoneTypeId(index) === 1 ? 'mobile' : 'landline';
    }

    getPrimaryTelephoneTypeId(index: number): number {
        return this._passengers[index]?.occupantContactDetails?.[0]?.telephoneNumberTypeId || 1;
    }

    setPrimaryTelephoneTypeId(index: number, value: number): void {
        const thirdParty = this._passengers[index];

        if (thirdParty?.occupantContactDetails?.[0]) {
            thirdParty.occupantContactDetails[0].telephoneNumberTypeId = value;
            this.updatePrimaryStatus(index);
        }
    }

    getSecondaryTelephoneNumber(index: number): string {
        return this._passengers[index]?.occupantContactDetails?.[1]?.telephoneNumber || '';
    }

    setSecondaryTelephoneNumber(index: number, value: string): void {
        const thirdParty = this._passengers[index];

        if (thirdParty?.occupantContactDetails?.[1]) {
            thirdParty.occupantContactDetails[1].telephoneNumber = value;
            this.updatePrimaryStatus(index);
        }
    }

    getSecondaryPhoneType(index: number): 'mobile' | 'landline' {
        return this.getSecondaryTelephoneTypeId(index) === 1 ? 'mobile' : 'landline';
    }

    getSecondaryTelephoneTypeId(index: number): number {
        return this._passengers[index]?.occupantContactDetails?.[1]?.telephoneNumberTypeId || 1;
    }

    setSecondaryTelephoneTypeId(index: number, value: number): void {
        const thirdParty = this._passengers[index];

        if (thirdParty?.occupantContactDetails?.[1]) {
            thirdParty.occupantContactDetails[1].telephoneNumberTypeId = value;
            this.updatePrimaryStatus(index);
        }
    }

    canAddPhoneAdditionalTp(index: number): boolean {
        const contactDetails = this._passengers[index].occupantContactDetails ?? [];
        return contactDetails.length < 2 && this.isPhoneNumberValid(contactDetails[0]?.telephoneNumber, contactDetails[0]?.telephoneNumberTypeId);
    }

    onPassengerInjuryChanged(typeIndex: number, passengerIndex: number): void {
        const passenger = this._passengers[passengerIndex];

        if (!passenger) {
            console.warn('Passenger not found.');
            return;
        }

        switch (typeIndex) {
            case 0: 
            case 2: 
                passenger.isInjured = true;
                break;
            case 1: 
            case 3: 
                passenger.isInjured = false;
                break;
            default:
                passenger.isInjured = null;
                break;
        }

        this.change.markForCheck();
    }

    private updatePrimaryStatus(index?: number): void {
        let contactDetails = this._passengers[index ?? 0]?.occupantContactDetails;

        if (!contactDetails) {
            return;
        }

        const primary = contactDetails[0];
        const secondary = contactDetails[1];

        if (primary) {
            primary.isPrimary = true;
        }

        if (secondary) {
            secondary.isPrimary = primary?.telephoneNumberTypeId !== secondary.telephoneNumberTypeId;
        }
    }

    get isRequired(): boolean {
        return this.stateService.formIsSubmitted;
    }

    onShareDataConsentChange(value: boolean): void {
        this._canShareDataWithPassengers = value;
        this.stateService.claim!.canShareDataWithPassengers = value;
    }

    get numberOfPassengers(): number {
        return this.stateService.claim!.numberOfPassengers;
    }
}
